// Central role hierarchy used by the sidebar filter and RoleGated route wrapper.
// Mirrors the API-side ROLE_RANK in packages/shared/src/roles.ts and
// apps/api/src/middleware/rbac.ts — updated to include `moderator` from
// extension 12.

export const ROLE_RANK: Record<string, number> = {
  student: 1,
  registrar: 2,
  teacher: 3,
  moderator: 4,
  tenant_admin: 5,
  super_admin: 6,
};

// Returns true if the user can manage Candidate Entry Slips: any
// dedicated `registrar` plus anyone tenant_admin or above. Mirrors the
// API's requireSlipManager() allowlist.
export function canManageSlips(userRole: string | undefined): boolean {
  if (userRole === "registrar") return true;
  return hasRole(userRole, "tenant_admin");
}

export type Role = keyof typeof ROLE_RANK;

// The ONE place a role is turned into words for a human. Labels were previously
// hardcoded at ~19 call sites, which is why they drifted.
//
// `student` deliberately reads "Candidate": the stored role value stays
// `student` (the API, JWT and DB all use it), but every piece of written
// content the same users read calls that person a candidate — the in-app help
// runs 28:1 candidate-to-student and the awarding-body centre documentation
// 150:5. Showing "Student" in a role picker while the help and the compliance
// pack say "candidate" is the inconsistency, not the fix.
export const ROLE_LABEL: Record<string, string> = {
  student: "Candidate",
  registrar: "Registrar",
  teacher: "Teacher",
  moderator: "Moderator",
  tenant_admin: "Tenant admin",
  super_admin: "Super admin",
};

// Unknown roles fall back to the raw value rather than an empty cell, so a role
// added server-side still renders something meaningful before the UI catches up.
export function roleLabel(role: string | undefined | null): string {
  if (!role) return "—";
  return ROLE_LABEL[role] ?? role;
}

// Assignable roles, most privileged first — the order the member/invite pickers
// already used. `super_admin` is NOT here: it is platform staff, granted out of
// band, never assignable from a tenant screen.
export const ASSIGNABLE_ROLES = [
  "tenant_admin",
  "moderator",
  "teacher",
  "registrar",
  "student",
] as const;

export function hasRole(userRole: string | undefined, minRole: string): boolean {
  const actual = ROLE_RANK[userRole ?? "student"] ?? 0;
  const required = ROLE_RANK[minRole] ?? 0;
  return actual >= required;
}
